<ng-container *transloco="let t; read: 'content'">
  <app-grid-page>
    <div class="flex min-h-full flex-col gap-4">
      <!-- Config toolbar: file + feed + stop, with save/assign actions. -->
      <div class="flex flex-wrap items-end gap-4 rounded-card bg-card p-4 shadow-hairline">
        <div class="flex min-w-0 flex-col gap-1.5">
          <span class="text-caption text-ink-2">{{ t('toolbar.svgApp') }}</span>
          <div class="flex h-12 min-w-0 items-center gap-3">
            <app-button variant="secondary" iconLeft="icon-import" (activated)="pickFile()">
              {{ t('toolbar.chooseFile') }}
            </app-button>
            <span class="max-w-56 truncate text-[14px] leading-5 text-ink-1">
              {{ fileName() || t('sampleFileName') }}
            </span>
          </div>
          <input
            #fileInput
            type="file"
            accept=".svg,image/svg+xml"
            class="hidden"
            (change)="onFileSelected($event)"
          />
        </div>

        <div class="w-px self-stretch bg-border-hairline"></div>

        <div class="flex w-60 flex-col gap-1.5">
          <span class="text-caption text-ink-2">{{ t('toolbar.feed') }}</span>
          <app-select
            [ngModel]="feedId()"
            (ngModelChange)="feedId.set($event)"
            [options]="feedOptions()"
            [placeholder]="t('toolbar.feedPlaceholder')"
          />
        </div>

        <div class="flex w-64 flex-col gap-1.5">
          <span class="text-caption text-ink-2">{{ t('toolbar.stopId') }}</span>
          <app-text-input
            [ngModel]="stopId()"
            (ngModelChange)="stopId.set($event)"
            [placeholder]="t('toolbar.stopIdPlaceholder')"
          />
        </div>

        <div class="flex-1"></div>

        <div class="flex items-center gap-3">
          <app-button
            variant="secondary"
            iconLeft="icon-menu-content1"
            [disabled]="cannotSave()"
            (activated)="saveContent()"
          >
            {{ t('toolbar.save') }}
          </app-button>
          <app-button
            variant="primary"
            iconLeft="icon-menu-displays1"
            [disabled]="cannotSave()"
            (activated)="openAssign()"
          >
            {{ t('toolbar.assign') }}
          </app-button>
        </div>
      </div>

      <!-- Preview / data / bindings card. min-h floor keeps the card (and its
           device frame) visible even when the flex parent has no resolved
           height; flex-1 lets it grow to fill a taller viewport. -->
      <div
        class="flex min-h-[760px] flex-1 flex-col overflow-hidden rounded-card bg-card shadow-hairline"
      >
        <!-- Tab strip + right-aligned controls. -->
        <div
          class="flex min-h-12 flex-shrink-0 flex-wrap-reverse items-end justify-between shadow-[inset_0_-1px_0_var(--border-strong)]"
        >
          <div class="flex h-12 flex-shrink-0 items-stretch whitespace-nowrap px-2">
            @for (tab of ['preview', 'data', 'bindings']; track tab) {
              <button
                type="button"
                class="ds-h3 relative flex items-center px-3 uppercase transition-colors"
                [class.text-ink-1]="this.tab() === tab"
                [class.text-ink-2]="this.tab() !== tab"
                (click)="showTab($any(tab))"
              >
                {{ t('tabs.' + tab) }}
                @if (this.tab() === tab) {
                  <span class="absolute inset-x-3 bottom-0 h-1 rounded-t bg-action"></span>
                }
              </button>
            }
          </div>
          <div class="ml-auto flex flex-shrink-0 items-center gap-4 whitespace-nowrap p-2">
            <app-button
              variant="secondary"
              size="sm"
              iconLeft="icon-update"
              [disabled]="!canRender()"
              (activated)="refresh()"
            >
              {{ t('controls.refresh') }}
            </app-button>
          </div>
        </div>

        <!-- PREVIEW -->
        @if (tab() === 'preview') {
          <div
            class="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 bg-sunken p-6"
            (dragover)="onDragOver($event)"
            (drop)="onDrop($event)"
          >
            <div class="flex min-h-0 w-full flex-1 items-center justify-center">
              <!-- Screen: fixed 13.3" portrait aspect, white e-paper surface,
                   no bezel — just a hairline + subtle shadow so it reads as a
                   panel against the sunken background. -->
              <div
                class="relative aspect-[1200/1600] w-[min(100%,560px)] overflow-hidden rounded-card bg-[var(--device-screen)] shadow-raised"
              >
                @if (previewUrl(); as url) {
                  <img
                    [src]="url"
                    [alt]="t('preview.alt')"
                    class="absolute inset-0 h-full w-full object-contain"
                  />
                } @else {
                  <div
                    class="absolute inset-0 flex items-center justify-center p-8 text-center text-[14px] leading-5 text-[var(--device-screen-ink)]"
                  >
                    {{ hasStop() ? t('preview.chooseFeed') : t('preview.enterStop') }}
                  </div>
                }
                @if (rendering()) {
                  <div
                    class="absolute inset-0 flex items-center justify-center text-[14px] text-[var(--device-screen-ink)]"
                    style="background: color-mix(in srgb, var(--device-screen) 60%, transparent)"
                  >
                    {{ t('preview.rendering') }}
                  </div>
                }
              </div>
            </div>

            <div class="flex flex-wrap justify-center gap-x-4 gap-y-2 text-caption text-ink-2">
              <span>{{ t('preview.spec') }}</span>
              @if (selectedFeed(); as feed) {
                <span>{{ feed.name }}<span> · </span>{{ stopId() || t('preview.noStop') }}</span>
              }
              <span>{{ t('preview.dropHint') }}</span>
            </div>

            @if (errorText()) {
              <span class="text-center text-caption text-danger">{{ errorText() }}</span>
            }
          </div>
        }

        <!-- SIRI DATA -->
        @if (tab() === 'data') {
          <div class="flex min-h-0 flex-1 flex-col gap-3 p-4">
            <div class="flex flex-wrap gap-x-6 gap-y-2 text-caption text-ink-2">
              <span class="font-mono">
                GET /api/v1/admin/content-delivery/tenants/{{ tenantId() }}/feeds/{{ feedId() }}/stops/{{ stopId() }}
              </span>
              @if (departureCount() !== null) {
                <span>{{ t('data.count', { count: departureCount() }) }}</span>
              }
            </div>
            <pre
              class="m-0 min-h-0 flex-1 overflow-auto rounded-control bg-sunken p-4 font-mono text-[12px] leading-[18px] text-ink-1 shadow-hairline"
            >{{ rawLoading() ? t('data.loading') : rawJson() }}</pre>
          </div>
        }

        <!-- BINDINGS -->
        @if (tab() === 'bindings') {
          <div class="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-4">
            <span class="pb-2 text-[14px] leading-5 text-ink-2">{{ t('bindings.intro') }}</span>
            @for (binding of bindings; track binding.token) {
              <div
                class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-4 py-2.5 text-[14px] leading-5 shadow-[inset_0_-1px_0_var(--border-hairline)]"
              >
                <code class="break-words font-mono text-[13px] text-ink-1">{{ binding.token }}</code>
                <span class="text-ink-2">{{ t('bindings.' + binding.descKey) }}</span>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </app-grid-page>

  <!-- Assign-to-display modal (stubbed confirm). -->
  <app-modal
    [(open)]="assignOpen"
    [title]="t('assign.title')"
    [primaryLabel]="checkedCount() > 1 ? t('assign.confirmMany', { count: checkedCount() }) : t('assign.confirm')"
    [primaryDisabled]="checkedCount() === 0"
    (primaryAction)="confirmAssign()"
    (closed)="assignOpen.set(false)"
  >
    <p class="pb-2 text-[14px] leading-5 text-ink-2">
      {{ selectedFeed() ? t('assign.hint', { name: appName() }) : '' }}
    </p>
    @for (display of displays(); track display.id) {
      <div
        class="flex items-center justify-between gap-3 py-2.5 shadow-[inset_0_-1px_0_var(--border-hairline)]"
      >
        <app-checkbox
          [ngModel]="display.checked"
          (ngModelChange)="toggleDisplay(display.id, $event)"
        >
          {{ display.name }}
        </app-checkbox>
        <span class="text-caption text-ink-2">
          {{ t('assign.deviceMeta') }}<span> · </span>{{ t('assign.' + display.metaKey) }}
        </span>
      </div>
    }
  </app-modal>
</ng-container>
